import { jsPDF } from 'jspdf';
import logoRound from '../assets/logo-round.jpg';

const ADDRESS_LINES = [
  'Opposite SBS Medical College',
  'Balawala, Dehradun',
  'Uttarakhand 248008'
];

export const CATALOGUE_STORE = {
  name: 'theVastra.store',
  tagline: 'Style That Speaks You',
  addressLines: ADDRESS_LINES,
  address: ADDRESS_LINES.join(', '),
  phone: '+91 80773 69863',
  email: 'thevastrastore1999@gmail.com',
  instagramHandle: '@theVastra.store',
  websiteLabel: 'thevastra.store'
};

export const CATALOGUE_LINKS = {
  maps: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`theVastra.store, ${ADDRESS_LINES.join(', ')}`)}`,
  whatsapp: 'https://wa.me/918077369863?text=Hi%20theVastra.store%2C%20I%20want%20to%20enquire%20about%20your%20collection',
  instagram: 'https://www.instagram.com/thevastra.store/',
  facebook: 'https://www.facebook.com/profile.php?id=61579074161205',
  website: 'https://thevastra.store',
  email: `mailto:${CATALOGUE_STORE.email}`,
  phone: 'tel:+918077369863'
};

export { logoRound as catalogueLogo };

const MAROON = [128, 28, 51];
const BLUSH = [247, 228, 232];
const CREAM = [255, 248, 246];
const PINK = [219, 39, 119];

export const MAX_PRODUCT_IMAGES = 6;

const toImageUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  return img.url || '';
};

const formatPrice = (price) => {
  if (price == null || price === '' || Number(price) === 0) return '';
  return `Rs. ${Number(price).toLocaleString('en-IN')}`;
};

// One wholesale set holds one piece of every size the product is offered in.
export const setSize = (item) => (item?.sizes || []).length;

const loadImageData = (url, maxW = 1400) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, maxW / img.naturalWidth);
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          data: canvas.toDataURL('image/jpeg', 0.86),
          w: canvas.width,
          h: canvas.height
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    const src = typeof url === 'string' && url.startsWith('http')
      ? (url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`)
      : url;
    img.src = src;
  });

// Every photo of a product sits in one row of equal tiles. The row is as tall as
// the tallest photo allows, and each photo is scaled to fit inside its tile, so
// nothing is cropped or stretched and all of them stay fully visible.
const drawImageRow = (pdf, images, x, y, totalW, maxH) => {
  const gap = 4;
  const tileW = (totalW - gap * (images.length - 1)) / images.length;
  const tallest = Math.max(...images.map(img => img.h / img.w));
  const tileH = Math.min(maxH, tileW * tallest);

  images.forEach((img, idx) => {
    const tileX = x + idx * (tileW + gap);
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(tileX, y, tileW, tileH, 2, 2, 'F');

    const scale = Math.min(tileW / img.w, tileH / img.h);
    const drawW = img.w * scale;
    const drawH = img.h * scale;
    pdf.addImage(img.data, 'JPEG', tileX + (tileW - drawW) / 2, y + (tileH - drawH) / 2, drawW, drawH);
  });

  return y + tileH;
};

const addClickable = (pdf, label, x, y, url, w = 42, h = 10) => {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(...MAROON);
  pdf.textWithLink(label, x, y, { url });
  const textW = pdf.getTextWidth(label);
  pdf.setDrawColor(...MAROON);
  pdf.setLineWidth(0.3);
  pdf.line(x, y + 1.2, x + textW, y + 1.2);
  pdf.link(x - 2, y - 6, Math.max(w, textW + 6), h, { url });
};

// The logo artwork sits on a solid background, so it is cropped to a circle
// with a transparent surround before it goes into the PDF.
const loadCircularLogo = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const out = 720;
        const canvas = document.createElement('canvas');
        canvas.width = out;
        canvas.height = out;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.arc(out / 2, out / 2, out / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        const side = Math.min(img.naturalWidth, img.naturalHeight) * 0.985;
        const sx = (img.naturalWidth - side) / 2;
        const sy = (img.naturalHeight - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
        resolve({ data: canvas.toDataURL('image/png'), w: out, h: out });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// The whole tile is one link target, so there is no separate "open" affordance.
const drawLinkTile = (pdf, { label, lines, url }, x, y, w, h) => {
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, w, h, 2.5, 2.5, 'F');

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(150, 140, 143);
  pdf.text(label.toUpperCase(), x + 6, y + 7);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9.5);
  pdf.setTextColor(...MAROON);
  const wrapped = lines.flatMap(line => pdf.splitTextToSize(line, w - 12));
  wrapped.slice(0, 3).forEach((line, i) => {
    pdf.text(line, x + 6, y + 14 + i * 4.6);
  });

  pdf.link(x, y, w, h, { url });
};

const drawLinkGrid = (pdf, tiles, startY, { x = 24, totalW = 162, gapX = 6, gapY = 6, tileH = 28 } = {}) => {
  const tileW = (totalW - gapX) / 2;
  tiles.forEach((tile, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    drawLinkTile(pdf, tile, x + col * (tileW + gapX), startY + row * (tileH + gapY), tileW, tileH);
  });
  return startY + Math.ceil(tiles.length / 2) * (tileH + gapY) - gapY;
};

// Retail and wholesale sit in two equal tiles. The value font size is measured
// once for the whole row so a long number shrinks both prices together and the
// two never differ in size or spill out of their tile.
const drawPriceRow = (pdf, prices, x, y, totalW) => {
  const gapX = 6;
  const tileW = (totalW - gapX) / 2;
  const hasNote = prices.some(p => p.note);
  const tileH = hasNote ? 26 : 21;
  const maxValueW = tileW - 10;

  pdf.setFont('helvetica', 'bold');
  let valueSize = 16;
  while (valueSize > 9) {
    pdf.setFontSize(valueSize);
    if (prices.every(p => pdf.getTextWidth(p.value) <= maxValueW)) break;
    valueSize -= 0.5;
  }

  prices.forEach((price, i) => {
    const tileX = x + i * (tileW + gapX);

    pdf.setFillColor(...BLUSH);
    pdf.roundedRect(tileX, y, tileW, tileH, 2.5, 2.5, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(150, 110, 122);
    pdf.text(price.label, tileX + 5, y + 7.5);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(valueSize);
    pdf.setTextColor(...price.color);
    pdf.text(price.value, tileX + 5, y + 16.5);

    if (price.note) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(120, 95, 105);
      pdf.text(pdf.splitTextToSize(price.note, tileW - 10)[0], tileX + 5, y + 22.5);
    }
  });

  return y + tileH;
};

const drawCover = (pdf, pageW, pageH, logoImage) => {
  pdf.setFillColor(...BLUSH);
  pdf.rect(0, 0, pageW, pageH, 'F');

  pdf.setFillColor(...CREAM);
  pdf.roundedRect(10, 10, pageW - 20, pageH - 20, 4, 4, 'F');

  const centerX = pageW / 2;

  if (logoImage) {
    const size = 68;
    pdf.addImage(logoImage.data, 'PNG', centerX - size / 2, 22, size, size);
  }

  pdf.setTextColor(...MAROON);
  pdf.setFont('times', 'italic');
  pdf.setFontSize(17);
  pdf.text('Welcome to', centerX, 104, { align: 'center' });

  pdf.setFont('times', 'bold');
  pdf.setFontSize(28);
  pdf.text(CATALOGUE_STORE.name, centerX, 120, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(90, 40, 55);
  pdf.text('Where Tradition Meets Style', centerX, 130, { align: 'center' });

  pdf.setDrawColor(210, 180, 190);
  pdf.setLineWidth(0.4);
  pdf.line(centerX - 34, 137, centerX + 34, 137);

  pdf.setFontSize(9.5);
  pdf.setTextColor(90, 90, 90);
  pdf.text('Your one-stop destination for Ethnic, Western & Daily Wear.', centerX, 145, { align: 'center' });

  const tiles = [
    { label: 'Store address', lines: CATALOGUE_STORE.addressLines, url: CATALOGUE_LINKS.maps },
    { label: 'Call / WhatsApp', lines: [CATALOGUE_STORE.phone], url: CATALOGUE_LINKS.whatsapp },
    { label: 'Email', lines: [CATALOGUE_STORE.email], url: CATALOGUE_LINKS.email },
    { label: 'Instagram', lines: [CATALOGUE_STORE.instagramHandle], url: CATALOGUE_LINKS.instagram },
    { label: 'Facebook', lines: [CATALOGUE_STORE.name], url: CATALOGUE_LINKS.facebook },
    { label: 'Website', lines: [CATALOGUE_STORE.websiteLabel], url: CATALOGUE_LINKS.website }
  ];

  const gridBottom = drawLinkGrid(pdf, tiles, 152);

  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8);
  pdf.setTextColor(140, 130, 133);
  pdf.text('Tap any tile to reach us directly.', centerX, gridBottom + 9, { align: 'center' });

  pdf.setFillColor(...MAROON);
  pdf.rect(10, pageH - 28, pageW - 20, 18, 'F');
  pdf.setTextColor(255, 248, 246);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('QUALITY', 48, pageH - 17, { align: 'center' });
  pdf.text('STYLE', centerX, pageH - 17, { align: 'center' });
  pdf.text('TRUST', pageW - 48, pageH - 17, { align: 'center' });
};

export const buildCataloguePdf = async (items) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const contentW = pageW - margin * 2;

  const logoImage = await loadCircularLogo(logoRound);

  drawCover(pdf, pageW, pageH, logoImage);

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    pdf.addPage();
    pdf.setFillColor(...CREAM);
    pdf.rect(0, 0, pageW, pageH, 'F');

    const imageUrls = (item.images || []).map(toImageUrl).filter(Boolean).slice(0, MAX_PRODUCT_IMAGES);
    const loaded = await Promise.all(
      imageUrls.map(url => loadImageData(url, imageUrls.length > 1 ? 900 : 1400))
    );
    const images = loaded.filter(Boolean);
    let y = margin;

    if (images.length) {
      y = drawImageRow(pdf, images, margin, y, contentW, 125) + 10;
    }

    pdf.setTextColor(...MAROON);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(20);
    const titleLines = pdf.splitTextToSize(item.title || 'Untitled', contentW);
    pdf.text(titleLines, margin, y);
    y += titleLines.length * 8 + 2;

    const retail = formatPrice(item.retailPrice ?? item.price);
    const wholesale = formatPrice(item.wholesalePrice);
    const setCount = setSize(item);
    const prices = [
      retail && { label: 'RETAIL PRICE', value: retail, color: PINK, note: 'Per piece' },
      wholesale && {
        label: 'WHOLESALE PRICE',
        value: wholesale,
        color: MAROON,
        note: setCount ? `Per set  ·  ${setCount} pcs (${(item.sizes || []).join(', ')})` : 'Per set'
      }
    ].filter(Boolean);

    if (prices.length) {
      y = drawPriceRow(pdf, prices, margin, y + 3, contentW) + 10;
    }

    pdf.setTextColor(70, 70, 70);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    const descLines = pdf.splitTextToSize(item.description || '', contentW);
    pdf.text(descLines, margin, y);
    y += descLines.length * 5.5 + 10;

    pdf.setDrawColor(220, 200, 205);
    pdf.line(margin, y, pageW - margin, y);
    y += 10;

    pdf.setTextColor(140, 140, 140);
    pdf.setFontSize(8);
    pdf.text('SIZES', margin, y);
    pdf.text('COLOURS', margin + contentW / 2, y);
    y += 6;

    pdf.setTextColor(40, 40, 40);
    pdf.setFontSize(11);
    const sizes = (item.sizes || []).join('  ·  ') || '—';
    const colors = (item.colors || []).join('  ·  ') || '—';
    pdf.text(pdf.splitTextToSize(sizes, contentW / 2 - 4), margin, y);
    pdf.text(pdf.splitTextToSize(colors, contentW / 2 - 4), margin + contentW / 2, y);

    addClickable(pdf, CATALOGUE_STORE.name, margin, pageH - 14, CATALOGUE_LINKS.website, 34);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(150, 140, 143);
    pdf.text(`${i + 2} / ${items.length + 1}`, pageW - margin, pageH - 14, { align: 'right' });
  }

  return pdf;
};

export const cataloguePdfFile = async (items) => {
  const pdf = await buildCataloguePdf(items);
  const blob = pdf.output('blob');
  return new File([blob], 'theVastra.store-Catalogue.pdf', { type: 'application/pdf' });
};
